import { useState, useEffect } from 'react';
import {
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  Archive,
  ExternalLink,
  Loader,
  Sparkles,
  TrendingUp,
  ChevronRight
} from 'lucide-react';
import { getProfessorInteractions, getEmails, getProfile } from '../lib/api';
import type { ProfessorInteraction, Email, Profile } from '../lib/types';
import { useNavigate, Link } from 'react-router-dom';
import { ResearchAssistant } from './ResearchAssistant';
// import { SubscriptionGate } from './SubscriptionGate';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';

type InteractionWithEmails = ProfessorInteraction & {
  emails?: Email[];
};

export function Dashboard() {
  const [interactions, setInteractions] = useState<InteractionWithEmails[]>([]);
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState<Email[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const navigate = useNavigate();
  // const [subscriptionTier, setSubscriptionTier] = useState<string>(
  //   SUBSCRIPTION_TIERS.FREE
  // );
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let mounted = true;
    let loadingTimeout: NodeJS.Timeout | null = null;

    const loadData = async () => {
      try {
        setLoading(true);
        
        loadingTimeout = setTimeout(() => {
          if (mounted) setLoading(false);
        }, 2000);
        
        const [interactionsData, emailsData] = await Promise.all([
          getProfessorInteractions(),
          getEmails(),
        ]);

        if (mounted) {
          const interactionsWithEmails = interactionsData.map(
            (interaction) => ({
              ...interaction,
              emails: emailsData.filter(
                (email) => email.interaction_id === interaction.id
              ),
            })
          );

          setInteractions(interactionsWithEmails);
          setEmails(emailsData);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        if (mounted) {
          if (loadingTimeout) clearTimeout(loadingTimeout);
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
      if (loadingTimeout) clearTimeout(loadingTimeout);
    };
  }, [navigate]);

  const filteredInteractions = interactions.filter(
    (interaction) =>
      selectedStatus === 'all' || interaction.status === selectedStatus
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-blue-900/30 text-blue-300 border border-blue-600/30';
      case 'contacted':
        return 'bg-yellow-900/30 text-yellow-300 border border-yellow-600/30';
      case 'responded':
        return 'bg-emerald-900/30 text-emerald-300 border border-emerald-600/30';
      case 'no_response':
        return 'bg-red-900/30 text-red-300 border border-red-600/30';
      case 'archived':
        return 'bg-gray-800/50 text-gray-300 border border-gray-700';
      default:
        return 'bg-gray-800/50 text-gray-300 border border-gray-700';
    }
  };

  const getStatusIconColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'text-blue-400';
      case 'contacted':
        return 'text-yellow-400';
      case 'responded':
        return 'text-emerald-400';
      case 'no_response':
        return 'text-red-400';
      case 'archived':
        return 'text-gray-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return <Mail className="h-4 w-4" />;
      case 'contacted':
        return <Clock className="h-4 w-4" />;
      case 'responded':
        return <CheckCircle className="h-4 w-4" />;
      case 'no_response':
        return <XCircle className="h-4 w-4" />;
      case 'archived':
        return <Archive className="h-4 w-4" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-12 w-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-indigo-500" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="space-y-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { 
            icon: <Mail className="h-6 w-6 text-indigo-400" />, 
            title: "Total Professors", 
            value: interactions.length,
            bgFrom: "from-indigo-900/40",
            bgTo: "to-indigo-800/20",
            delay: 0.1
          },
          { 
            icon: <CheckCircle className="h-6 w-6 text-emerald-400" />, 
            title: "Responses Received", 
            value: interactions.filter((i) => i.status === 'responded').length,
            bgFrom: "from-emerald-900/40",
            bgTo: "to-emerald-800/20",
            delay: 0.2
          },
          { 
            icon: <Clock className="h-6 w-6 text-yellow-400" />, 
            title: "Awaiting Response", 
            value: interactions.filter((i) => i.status === 'contacted').length,
            bgFrom: "from-yellow-900/40",
            bgTo: "to-yellow-800/20",
            delay: 0.3
          },
          { 
            icon: <TrendingUp className="h-6 w-6 text-purple-400" />, 
            title: "Total Emails Sent", 
            value: emails.length,
            bgFrom: "from-purple-900/40",
            bgTo: "to-purple-800/20",
            delay: 0.4
          }
        ].map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: stat.delay, duration: 0.3 }}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            className={`bg-gradient-to-br ${stat.bgFrom} ${stat.bgTo} rounded-xl overflow-hidden shadow-lg`}
          >
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 p-2 bg-gray-800/50 rounded-lg">
                  {stat.icon}
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-400 truncate">
                      {stat.title}
                    </dt>
                    <dd className="text-2xl font-bold text-white">
                      {stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.3 }}
        className="bg-gray-800 rounded-xl overflow-hidden shadow-lg"
      >
        <ResearchAssistant />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.3 }}
        className="bg-gray-800 rounded-xl overflow-hidden shadow-lg"
      >
        <div className="p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">
              Professor Interactions
            </h3>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <p className="text-sm text-gray-400">
              Your interactions with professors and their current status.
            </p>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="ml-4 rounded-lg bg-gray-900 border border-gray-600 text-gray-300 text-sm focus:ring-indigo-500 focus:border-indigo-500 px-3 py-1.5"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="responded">Responded</option>
              <option value="no_response">No Response</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
        <div className="border-t border-gray-700">
          <AnimatePresence>
            <ul role="list" className="divide-y divide-gray-700">
              {filteredInteractions.map((interaction, index) => (
                <motion.li 
                  key={interaction.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.3 }}
                  exit={{ opacity: 0, height: 0 }}
                  whileHover={{ backgroundColor: 'rgba(75, 85, 99, 0.3)' }}
                >
                  <div className="px-6 py-5 flex items-center justify-between transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-lg font-medium text-white">
                              {interaction.professor_name.replace(
                                /^(Dr\.|Professor|Prof\.)\s+/i,
                                ''
                              )}
                            </h4>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(interaction.status)}`}
                            >
                              <span className={`mr-1 ${getStatusIconColor(interaction.status)}`}>
                                {getStatusIcon(interaction.status)}
                              </span>
                              <span className="capitalize">
                                {interaction.status.replace('_', ' ')}
                              </span>
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-gray-400">
                            {interaction.university}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <div className="flex items-center text-sm text-gray-400">
                          <span className="truncate">{interaction.notes}</span>
                        </div>
                        {interaction.emails && interaction.emails.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {interaction.emails.map((email) => (
                              <span
                                key={email.id}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-700 text-gray-300 border border-gray-600"
                              >
                                <Mail className="h-3 w-3 mr-1 text-indigo-400" />
                                {new Date(email.created_at).toLocaleDateString()}
                                {email.sent_at && ' (Sent)'}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-6 flex items-center space-x-4">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() =>
                          navigate('/emails', {
                            state: { interactionId: interaction.id },
                          })
                        }
                        className="inline-flex items-center px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-lg shadow-indigo-900/20"
                      >
                        <Mail className="h-4 w-4 mr-1.5" />
                        Email
                      </motion.button>
                      {interaction.website && (
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() =>
                            interaction.website &&
                            window.open(interaction.website, '_blank')
                          }
                          className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 hover:text-white transition-colors duration-200"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </motion.button>
                      )}
                    </div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}
